import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { ChevronLeft, ChevronRight, Users, Clock, Calendar, Download, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const TeamTimesheet = () => {
  const { user, api } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [teamData, setTeamData] = useState(null);
  const [selectedProject, setSelectedProject] = useState('all');

  // Calculate week start based on offset
  const getWeekStart = (offset = 0) => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    const monday = new Date(now.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  const weekStart = useMemo(() => getWeekStart(weekOffset), [weekOffset]);

  // Format duration
  const formatDuration = (hours) => {
    if (!hours || hours <= 0) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: date.toLocaleString('en-US', { month: 'short' })
    };
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const badges = {
      draft: { bg: 'bg-slate-100 dark:bg-slate-700', text: 'text-slate-600 dark:text-slate-300', label: 'Draft' },
      submitted: { bg: 'bg-amber-100 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', label: 'Pending' },
      approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', label: 'Approved' },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', label: 'Rejected' }
    };
    return badges[status] || badges.draft;
  };

  // Fetch team timesheet data
  useEffect(() => {
    const fetchData = async () => {
      if (!api) return;
      setLoading(true);
      try {
        const response = await api.get(`/timesheets/team/week/${weekStart}`);
        setTeamData(response.data);
      } catch (error) {
        console.error('Error fetching team timesheet:', error);
        toast.error('Failed to load team timesheet');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, weekStart]);

  // Filtered data based on selected project
  const filteredData = useMemo(() => {
    if (!teamData) return null;
    if (selectedProject === 'all') return teamData;

    const filteredEmployees = teamData.employees.map(emp => {
      const projHours = emp.project_hours?.[selectedProject];
      if (!projHours) {
        const emptyDaily = {};
        teamData.week_dates.forEach(d => emptyDaily[d] = 0);
        return { ...emp, daily_hours: emptyDaily, total_hours: 0 };
      }
      return {
        ...emp,
        daily_hours: projHours.daily,
        total_hours: Math.round(projHours.total * 100) / 100
      };
    }).filter(emp => emp.total_hours > 0);

    const teamDailyTotals = {};
    teamData.week_dates.forEach(d => {
      teamDailyTotals[d] = filteredEmployees.reduce((sum, e) => sum + (e.daily_hours[d] || 0), 0);
    });

    return {
      ...teamData,
      employees: filteredEmployees,
      team_totals: {
        daily: teamDailyTotals,
        total: filteredEmployees.reduce((sum, e) => sum + e.total_hours, 0)
      }
    };
  }, [teamData, selectedProject]);

  // Export to CSV
  const handleExport = () => {
    if (!filteredData) return;
    
    const headers = ['Employee', 'Department', 'Status', ...filteredData.week_dates.map(d => {
      const { day, date, month } = formatDate(d);
      return `${day} ${date}`;
    }), 'Total'];
    
    const rows = filteredData.employees.map(emp => {
      const dailyHours = filteredData.week_dates.map(d => (emp.daily_hours[d] || 0).toFixed(2));
      return [
        `"${emp.name}"`,
        `"${emp.department || '-'}"`,
        emp.timesheet_status,
        ...dailyHours,
        emp.total_hours.toFixed(2)
      ].join(',');
    });
    
    // Add totals row
    const totalRow = [
      'TOTAL',
      '',
      '',
      ...filteredData.week_dates.map(d => (filteredData.team_totals.daily[d] || 0).toFixed(2)),
      filteredData.team_totals.total.toFixed(2)
    ].join(',');
    
    const csv = [headers.join(','), ...rows, totalRow].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-timesheet-${weekStart}${selectedProject !== 'all' ? '-' + selectedProject : ''}.csv`;
    a.click();
    toast.success('Timesheet exported');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="team-timesheet">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            {user?.role === 'admin' ? t('nav.teamTimesheet') : t('nav.teamTimesheet')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('timesheet.subtitle')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Project Filter */}
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="h-10 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 px-3 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="project-filter-select"
          >
            <option value="all">All Projects</option>
            {teamData?.projects?.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {/* Week Navigation */}
          <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset(prev => prev - 1)}
              className="h-8 w-8 p-0 rounded-lg"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="px-3 text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[120px] text-center">
              {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : 
                new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeekOffset(prev => prev + 1)}
              disabled={weekOffset >= 0}
              className="h-8 w-8 p-0 rounded-lg"
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Export Button */}
          <Button
            variant="outline"
            onClick={handleExport}
            className="h-10 rounded-xl"
            data-testid="export-team-timesheet-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="premium-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Employees</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{filteredData?.employees?.length || 0}</p>
            </div>
          </div>
        </div>
        <div className="premium-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Total Hours</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{formatDuration(filteredData?.team_totals?.total || 0)}</p>
            </div>
          </div>
        </div>
        <div className="premium-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Approved</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {teamData?.employees?.filter(e => e.timesheet_status === 'approved').length || 0} / {filteredData?.employees?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timesheet Grid */}
      <div className="premium-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left p-4 font-semibold text-sm text-slate-600 dark:text-slate-400 min-w-[200px]">
                  Employee
                </th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600 dark:text-slate-400 w-24">
                  Status
                </th>
                {filteredData?.week_dates?.map(date => {
                  const { day, date: d, month } = formatDate(date);
                  const isWeekend = day === 'Sat' || day === 'Sun';
                  return (
                    <th 
                      key={date} 
                      className={`text-center p-4 font-semibold text-sm min-w-[80px] ${
                        isWeekend ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-400' : 'text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      <div className="text-xs">{day}</div>
                      <div>{d} {month}</div>
                    </th>
                  );
                })}
                <th className="text-center p-4 font-semibold text-sm text-slate-600 dark:text-slate-400 min-w-[80px] bg-blue-50 dark:bg-blue-900/10">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData?.employees?.map((employee, idx) => {
                const statusBadge = getStatusBadge(employee.timesheet_status);
                return (
                  <tr 
                    key={employee.id}
                    className={`border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                      idx % 2 === 0 ? '' : 'bg-slate-25 dark:bg-slate-800/25'
                    }`}
                    data-testid={`employee-row-${employee.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold overflow-hidden">
                          {employee.profile_photo ? (
                            <img src={employee.profile_photo} alt={employee.name} className="w-full h-full object-cover" />
                          ) : (
                            employee.name?.charAt(0)?.toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white text-sm">{employee.name}</p>
                          <p className="text-xs text-slate-500">{employee.department || employee.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    {filteredData?.week_dates?.map(date => {
                      const hours = employee.daily_hours[date] || 0;
                      const { day } = formatDate(date);
                      const isWeekend = day === 'Sat' || day === 'Sun';
                      return (
                        <td 
                          key={date}
                          className={`p-4 text-center text-sm ${
                            isWeekend ? 'bg-slate-50 dark:bg-slate-700/30' : ''
                          } ${hours > 0 ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-400'}`}
                        >
                          {hours > 0 ? formatDuration(hours) : '-'}
                        </td>
                      );
                    })}
                    <td className="p-4 text-center font-semibold text-slate-900 dark:text-white bg-blue-50 dark:bg-blue-900/10">
                      {formatDuration(employee.total_hours)}
                    </td>
                  </tr>
                );
              })}
              
              {/* Totals Row */}
              <tr className="border-t-2 border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700/50 font-semibold">
                <td className="p-4 text-slate-700 dark:text-slate-300">
                  Team Total
                </td>
                <td className="p-4"></td>
                {filteredData?.week_dates?.map(date => {
                  const total = filteredData.team_totals?.daily[date] || 0;
                  return (
                    <td key={date} className="p-4 text-center text-slate-700 dark:text-slate-300">
                      {formatDuration(total)}
                    </td>
                  );
                })}
                <td className="p-4 text-center text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20">
                  {formatDuration(filteredData?.team_totals?.total || 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Empty State */}
        {(!filteredData?.employees || filteredData.employees.length === 0) && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No employees found</p>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default TeamTimesheet;
